import { Group, Text, Grid, Select } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { DateInput } from '@mantine/dates';
import dayjs from 'dayjs';
import i18next from 'i18next';
import { PortalButton } from 'components/Button/PortalButton';
import { useDrawerContext } from 'context/hooks';
import { FormColumn } from 'components/Form/FormColumn';
import { useForm } from '@mantine/form';
import { CreateReportValues } from 'models/report';
import { useCustomMutation } from 'hooks/api/useCustomMutation';
import { getGroupReportConfiguration } from 'services/groups';
import { ReportContentForm } from 'components/Form/ReportContentForm';
import { MAX_REPORTS_TOTAL } from 'utils/general';
import { createReport } from 'services/reports';
import { FileType, downloadFile } from 'utils/fileManagement';
import { usePatient } from 'hooks/api/useUser';
import { PatientIdProps } from 'models/general';
import { generalDateFormat } from 'utils/dates';
import { initialReportContentSetting } from '../../../utils/reports';

export const CreateReportsForm = ({ patientId }: PatientIdProps) => {
  const { t } = useTranslation();
  const { closeDrawer } = useDrawerContext();
  const { data: patientData } = usePatient({ patientId });
  const { mutate: createNewReport, isPending: isCreating } = useCustomMutation({
    fn: createReport,
    onSuccess: {
      callback: (data) => {
        if (!(data instanceof Error) && data?.reportUrl) {
          downloadFile(data?.reportUrl, data.reportId, FileType.measurement);
          URL.revokeObjectURL(data.reportUrl);
          closeDrawer();
        }
      },
    },
  });
  const activeGroups = patientData?.visibleEnlistments || [];
  const form = useForm<CreateReportValues>({
    mode: 'uncontrolled',
    initialValues: {
      userId: patientData?.id,
      startDate: dayjs().subtract(1, 'month').toDate(),
      endDate: dayjs().toDate(),
      reportContent: initialReportContentSetting,
      reportsTotal: 0,
    },
    validate: {
      reportsTotal: (value) => (value > MAX_REPORTS_TOTAL || value < 0 ? t('validations_reports_total') : null),
      startDate: (value, values) => (value > values.endDate ? t('validations_date_range') : null),
    },
    onValuesChange: (values) => {
      form.setFieldValue(
        'reportsTotal',
        Object.values(values.reportContent).reduce((sum, v) => sum + Number(v), 0),
      );
    },
  });

  const { mutate: updateReportContent } = useCustomMutation({
    fn: getGroupReportConfiguration,
    onSuccess: {
      /* Make sure that all indicators are initialized before assigning numbers from the group configuration */
      callback: (data) => {
        form.setValues({
          groupId: data.id,
          reportContent: { ...initialReportContentSetting, ...data.reportContent },
        });
      },
    },
  });

  const handleGroupChanged = (groupId: string | null) => {
    if (!groupId) return;
    form.setValues({ groupId });
    updateReportContent(groupId);
  };

  if (!form.getValues().groupId && activeGroups.length === 1) handleGroupChanged(activeGroups[0].groupId);

  return (
    <form onSubmit={form.onSubmit((values) => createNewReport(values))}>
      <Grid>
        <FormColumn label={t('group_label')} span={[4, 7]}>
          <Select
            data={activeGroups.map((group) => ({
              label: group.name,
              value: group.groupId,
            }))}
            placeholder={t('group_placeholder')}
            value={form.getValues().groupId}
            onChange={(value) => handleGroupChanged(value)}
            disabled={isCreating}
          />
        </FormColumn>
        <FormColumn label={t('date_range_label')} span={[4, 8]}>
          <Group>
            <DateInput
              locale={i18next.language}
              maxDate={dayjs().toDate()}
              {...form.getInputProps('startDate')}
              disabled={isCreating}
              valueFormat={generalDateFormat}
            />
            <Text>{t('to_label')}</Text>
            <DateInput
              locale={i18next.language}
              maxDate={dayjs().toDate()}
              {...form.getInputProps('endDate')}
              disabled={isCreating}
              valueFormat={generalDateFormat}
            />
          </Group>
        </FormColumn>
        <FormColumn span={[4, 7]}>
          <ReportContentForm form={form} disabled={!form.getValues().groupId || isCreating} />
        </FormColumn>
        <Grid.Col span={11}>
          <Group justify={'flex-start'}>
            <PortalButton
              type={'submit'}
              label={t('patients_create_report')}
              loading={isCreating}
              disabled={
                !form.getValues().groupId ||
                form.getValues().reportsTotal <= 0 ||
                form.getValues().reportsTotal > MAX_REPORTS_TOTAL
              }
            />
            <PortalButton
              label={t('cancel_label')}
              onClick={() => closeDrawer()}
              variant={'outline'}
              disabled={isCreating}
            />
          </Group>
        </Grid.Col>
      </Grid>
    </form>
  );
};
